import { Dayjs } from 'dayjs';

import { AiLoadSummary } from '~hooks/useAiSummaries/models';

/**
 * Hot pink (MUI pink 500) for everything AI-deduced on the map and in the
 * sidebar. Chosen for contrast against everything it overlaps: the blue
 * platform geofence fills (where purple blended in), the #FF8C00 GPS trace,
 * and the truck-marker green.
 */
export const AI_SUMMARY_MAP_COLOR = '#E91E63';

export interface AiSummaryCircleVisit {
  /** 0-based index of the deduced trip that used this circle. */
  loadIndex: number;
  enteredAt: Dayjs | null;
  exitedAt: Dayjs | null;
}

export interface AiSummaryCircle {
  key: string;
  center: { lat: number; lng: number };
  radiusM: number;
  side: 'dropoff' | 'pickup';
  /** Model-assigned label for the deduced location. */
  name: string;
  /** True when the deduced location matched a configured platform Site. */
  isConfiguredSite: boolean;
  /** Per-trip dwell windows that produced this circle; each visit carries its own trip index. */
  visits: AiSummaryCircleVisit[];
}

/**
 * Collapses the per-trip denormalized pickup/dropoff geofences into unique circles for the
 * map overlay. The same site pair repeats on every trip of the job, so identical circles
 * are rendered once and labeled with all of the trip indexes that used them.
 */
export function buildAiSummaryCircles(aiLoadSummaries: AiLoadSummary[]) {
  const circlesByKey = new Map<string, AiSummaryCircle>();

  aiLoadSummaries.forEach((aiLoadSummary) => {
    const sides = [
      {
        side: 'pickup' as const,
        geofence: aiLoadSummary.pickupGeofence,
        siteId: aiLoadSummary.pickupSiteId,
        enteredAt: aiLoadSummary.pickupEnteredAt,
        exitedAt: aiLoadSummary.pickupExitedAt,
      },
      {
        side: 'dropoff' as const,
        geofence: aiLoadSummary.dropoffGeofence,
        siteId: aiLoadSummary.dropoffSiteId,
        enteredAt: aiLoadSummary.dropoffEnteredAt,
        exitedAt: aiLoadSummary.dropoffExitedAt,
      },
    ];

    sides.forEach(({ side, geofence, siteId, enteredAt, exitedAt }) => {
      if (!geofence) return;

      const { lat, lon } = geofence.circleCenter;
      const key = `${side}:${lat}:${lon}:${geofence.circleRadius}`;
      const visit = { loadIndex: aiLoadSummary.loadIndex, enteredAt, exitedAt };
      const existing = circlesByKey.get(key);
      if (existing) {
        existing.visits.push(visit);
      } else {
        circlesByKey.set(key, {
          key,
          center: { lat, lng: lon },
          radiusM: geofence.circleRadius,
          side,
          name: geofence.name,
          isConfiguredSite: siteId !== null,
          visits: [visit],
        });
      }
    });
  });

  return Array.from(circlesByKey.values());
}

export interface AiSummarySiteVisit {
  /** 1-based trip number, matching the labels used elsewhere on this page. */
  loadNumber: number;
  side: 'dropoff' | 'pickup';
  enteredAt: Dayjs | null;
  exitedAt: Dayjs | null;
}

/** The subset of a site's data the map needs to zoom to it. */
export interface AiSiteFocus {
  key: string;
  circles: { center: { lat: number; lng: number }; radiusM: number }[];
}

export interface AiSummarySite extends AiSiteFocus {
  name: string;
  sides: ('dropoff' | 'pickup')[];
  isConfiguredSite: boolean;
  /** Sorted by trip number; exposes the per-trip visits and windows the list expands to. */
  visits: AiSummarySiteVisit[];
}

/**
 * Groups the deduced circles by the model's place label so repeated visits to the same
 * physical location — a shuttle's round trips, or a corridor of paving dwells — render as
 * one AI site with multiple geofences, the same way a configured Site groups multiple
 * next_billion_geofences. The label is model-assigned free text and can drift within a run
 * (e.g. "Hydrant" vs "Hydrant area" for the same place), so this by-name grouping is an
 * interim identity key until the model contract gives places a stable ID.
 */
export function groupAiSummaryCirclesByName(circles: AiSummaryCircle[]): AiSummarySite[] {
  const sitesByName = new Map<string, AiSummarySite>();

  circles.forEach((circle) => {
    const site = sitesByName.get(circle.name) ?? {
      key: circle.name,
      name: circle.name,
      sides: [],
      // a site reads as "configured" only when every one of its circles matched a
      // platform site; one discovered circle among matched ones still counts as discovered.
      isConfiguredSite: true,
      visits: [],
      circles: [],
    };

    if (!site.sides.includes(circle.side)) {
      site.sides.push(circle.side);
    }
    if (!circle.isConfiguredSite) {
      site.isConfiguredSite = false;
    }
    // A location used as both pickup and dropoff produces two AiSummaryCircle
    // entries with identical geometry; keep one so the geofence count is honest.
    const alreadyHasThisGeometry = site.circles.some(
      (existingCircle) =>
        existingCircle.center.lat === circle.center.lat &&
        existingCircle.center.lng === circle.center.lng &&
        existingCircle.radiusM === circle.radiusM,
    );
    if (!alreadyHasThisGeometry) {
      site.circles.push({ center: circle.center, radiusM: circle.radiusM });
    }
    circle.visits.forEach((visit) => {
      site.visits.push({
        loadNumber: visit.loadIndex + 1,
        side: circle.side,
        enteredAt: visit.enteredAt,
        exitedAt: visit.exitedAt,
      });
    });

    sitesByName.set(circle.name, site);
  });

  const sites = Array.from(sitesByName.values());
  sites.forEach((site) =>
    site.visits.sort(
      (a, b) => a.loadNumber - b.loadNumber || a.side.localeCompare(b.side),
    ),
  );

  return sites;
}

// meters per degree of latitude (and of longitude at the equator) on the WGS84 sphere
const METERS_PER_DEGREE = 111_320;
// enough vertices for a circle outline that looks smooth at admin-geo zoom levels
const CIRCLE_OUTLINE_POINT_COUNT = 64;

/**
 * Approximates a circle as a closed polyline path. Google Maps `Circle` overlays cannot
 * render dashed strokes, so the AI overlay draws each circle as a polyline using the
 * shared dashed gap options instead.
 */
export function circleOutlinePath(center: { lat: number; lng: number }, radiusM: number) {
  const latitudeRadians = (center.lat * Math.PI) / 180;

  return Array.from({ length: CIRCLE_OUTLINE_POINT_COUNT + 1 }, (_, index) => {
    const angle = (2 * Math.PI * index) / CIRCLE_OUTLINE_POINT_COUNT;

    return {
      lat: center.lat + (radiusM * Math.sin(angle)) / METERS_PER_DEGREE,
      lng:
        center.lng +
        (radiusM * Math.cos(angle)) / (METERS_PER_DEGREE * Math.cos(latitudeRadians)),
    };
  });
}
